import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store'
import { updatePreviewResponse } from '@/store/slices/preview'
import { cn } from '@/lib/utils'

interface RadioProps {
    onAutoProceed?: () => void
    onOtherSelected?: (questionId: string) => void
    onRegularOptionSelected?: (questionId: string) => void
    isTransitioning?: boolean
}

const Radio: React.FC<RadioProps> = ({
    onAutoProceed,
    onOtherSelected,
    onRegularOptionSelected,
    isTransitioning = false,
}) => {
    const dispatch = useDispatch()
    const { selectedQuestionId } = useSelector((state: any) => state.preview)
    // Get question data from Redux store
    const question = useSelector((state: RootState) =>
        state.preview.questions.find(
            (q: any) => q.questionId === selectedQuestionId
        )
    )

    const savedResponse = useSelector(
        (state: RootState) => state.preview.previewResponses[selectedQuestionId]
    )

    // Extract options and other configuration from question
    const options = question?.config?.options || []
    const allowOther = question?.config?.allowOther || false
    const otherLabel = question?.config?.otherLabel || 'Other'

    const [selectedOption, setSelectedOption] = useState<string | null>(
        savedResponse?.optionId || null
    )
    const [otherValue, setOtherValue] = useState<string>(
        savedResponse?.otherValue || ''
    )

    // Update local state when Redux store changes
    useEffect(() => {
        if (savedResponse) {
            setSelectedOption(savedResponse.optionId || null)
            setOtherValue(savedResponse.otherValue || '')
        } else {
            setSelectedOption(null)
            setOtherValue('')
        }
    }, [savedResponse])

    useEffect(() => {
        if (selectedOption) {
            dispatch(
                updatePreviewResponse({
                    questionId: selectedQuestionId,
                    response: {
                        optionId: selectedOption,
                        otherValue:
                            selectedOption === 'other' ? otherValue : '',
                    },
                })
            )
        }
    }, [selectedOption, otherValue, dispatch, selectedQuestionId])

    const handleOptionSelect = (optionId: string, isOtherOption: boolean = false) => {
        // Prevent selection during transition
        if (isTransitioning) return

        setSelectedOption(optionId)

        if (isOtherOption) {
            // "Other" option selected - notify parent and don't auto-proceed
            onOtherSelected?.(selectedQuestionId)
        } else {
            // Regular option selected - notify parent and trigger auto-proceed
            onRegularOptionSelected?.(selectedQuestionId)

            // Auto-proceed after brief delay for visual feedback
            if (onAutoProceed) {
                setTimeout(() => {
                    onAutoProceed()
                }, 300)
            }
        }
    }

    const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOtherValue(e.target.value)
        if (selectedOption === 'other') {
            dispatch(
                updatePreviewResponse({
                    questionId: selectedQuestionId,
                    response: {
                        optionId: 'other',
                        otherValue: e.target.value,
                    },
                })
            )
        }
    }

    // If question not found, return null
    if (!question) return null

    const displayType = question?.config?.displayType || 'radio'

    if (displayType === 'pill') {
        return (
            <div
                className={`w-full flex flex-wrap gap-3 mt-6`}
                key={`${selectedQuestionId}-radio-pill`}
            >
                {options.map(
                    (option: { id: string; text: string }, index: number) => (
                        <button
                            key={option.id}
                            id={`${selectedQuestionId}-${option.id}`}
                            type="button"
                            className={cn(
                                'h-[48px] rounded-md px-4 text-[14px] font-[500] transition-all duration-150 shadow-none',
                                selectedOption === option.id
                                    ? 'border-[#005BD3] bg-[#005BD3]/10 border'
                                    : 'border-[#D9D9D9] border'
                            )}
                            onClick={() => handleOptionSelect(option.id, false)}
                            tabIndex={0}
                        >
                            {option?.text !== ''
                                ? option.text
                                : `Response ${index + 1}`}
                        </button>
                    )
                )}
                {allowOther && (
                    <button
                        type="button"
                        id={`${selectedQuestionId}-other`}
                        className={cn(
                            'h-[48px] rounded-md px-4 text-[14px] font-[500] transition-all duration-150 shadow-none',
                            selectedOption === 'other'
                                ? 'border-[#005BD3] bg-[#005BD3]/10 border'
                                : 'border-[#D9D9D9] border'
                        )}
                        onClick={() => handleOptionSelect('other', true)}
                        tabIndex={0}
                    >
                        {otherLabel}
                    </button>
                )}
                {selectedOption === 'other' && (
                    <input
                        type="text"
                        key={`${selectedQuestionId}-other-input`}
                        value={otherValue}
                        onChange={handleOtherChange}
                        className={cn(
                            'mt-2 w-full outline-none rounded-md placeholder-[#616161] border border-[0.5px] border-[#8A8A8A] bg-background px-3 py-2 text-sm shadow-none',
                            otherValue !== '' ? 'font-[550]' : 'font-[400]'
                        )}
                        placeholder="Please specify"
                        style={{ maxWidth: 414 }}
                    />
                )}
            </div>
        )
    }

    return (
        <div className="relative radio-group mt-6 flex flex-col gap-[14px]">
            {options.map((option, index) => (
                <div
                    key={`${selectedQuestionId}-${option.id}-modal`}
                    className="radio-option"
                >
                    <RadioButton
                        id={`${selectedQuestionId}-${option.id}`}
                        checked={selectedOption === option.id}
                        onChange={() => handleOptionSelect(option.id, false)}
                        label={
                            option?.text !== ''
                                ? option.text
                                : `Response ${index + 1}`
                        }
                    />
                </div>
            ))}
            {allowOther && (
                <div className="relative radio-option">
                    <RadioButton
                        id={`${selectedQuestionId}-other`}
                        checked={selectedOption === 'other'}
                        onChange={() => handleOptionSelect('other', true)}
                        label={otherLabel}
                    />
                    {selectedOption === 'other' && (
                        <input
                            type="text"
                            value={otherValue}
                            onChange={handleOtherChange}
                            className={cn(
                                'mt-2 ml-7 w-[414px] outline-none rounded-md placeholder-[#616161]  border border-[0.5px] border-[#8A8A8A] bg-background px-3 py-2 text-sm shadow-none',
                                otherValue !== '' ? 'font-[550]' : 'font-[400]'
                            )}
                            placeholder="Please specify"
                        />
                    )}
                </div>
            )}
        </div>
    )
}

interface RadioButtonProps {
    id: string
    checked: boolean
    onChange: () => void
    label: string
}

const RadioButton: React.FC<RadioButtonProps> = ({
    id,
    checked,
    onChange,
    label,
}) => {
    return (
        <label
            key={`${id}-preview`}
            htmlFor={id + 'preview'}
            className="flex items-center cursor-pointer text-[14px] font-[400] select-none gap-[11px]"
        >
            <div className="relative flex-shrink-0 w-[18px] h-[18px]">
                <input
                    type="radio"
                    id={id + 'preview'}
                    key={`${id}-preview-input`}
                    checked={checked}
                    onChange={onChange}
                    className="sr-only"
                />
                <span
                    className={`inline-block w-[18px] h-[18px] rounded-full transition-colors ${
                        checked ? 'bg-[#015AD1]' : 'border border-[#DEDEDE]'
                    } group-hover:bg-gray-300`}
                >
                    {checked && (
                        <span className="block w-[6px] h-[6px] rounded-full bg-white mx-auto my-[6px]"></span>
                    )}
                </span>
            </div>
            <span>{label}</span>
        </label>
    )
}

export default Radio
